import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { items, subtotal, deliveryFee, total, pincode, pincodeInfo, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const placedRef = useRef(false);

  const [slots, setSlots] = useState([]);
  const [onlineEnabled, setOnlineEnabled] = useState(false);
  const [date, setDate] = useState(addDays(new Date(), 1));
  const [slot, setSlot] = useState("");
  const [address, setAddress] = useState({
    full_name: user?.name || "",
    phone: "",
    line1: "",
    line2: "",
    city: pincodeInfo?.city || "",
    state: pincodeInfo?.state || "",
    pincode: pincode || "",
  });
  const [payMethod, setPayMethod] = useState("cod");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (placedRef.current) return;
    if (!user) { toast.error("Please sign in to checkout"); navigate("/login?next=/checkout"); return; }
    if (items.length === 0) { navigate("/cart"); return; }
    if (!pincodeInfo?.deliverable) { toast.error("Please verify your pincode first"); navigate("/cart"); return; }
    api.get("/slots").then((r) => { setSlots(r.data); if (r.data[0]) setSlot(r.data[0].label); });
    const fetchPayKey = () =>
      api.get("/payments/razorpay/key").then((r) => {
        setOnlineEnabled(!!r.data.enabled);
        if (r.data.enabled) setPayMethod("online");
      }).catch(() => setOnlineEnabled(false));
    fetchPayKey();
    // Retry once after 3s in case the backend was restarting during first load
    const retry = setTimeout(fetchPayKey, 3000);
    return () => clearTimeout(retry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, items.length, pincodeInfo?.deliverable]);

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const placeOrder = async () => {
    if (!address.full_name || !address.phone || !address.line1) { toast.error("Please fill your address"); return; }
    if (!slot) { toast.error("Please pick a delivery slot"); return; }
    setPlacing(true);
    try {
      const payload = {
        items,
        address,
        delivery_date: format(date, "yyyy-MM-dd"),
        delivery_slot: slot,
        payment_method: payMethod,
        subtotal, delivery_fee: deliveryFee, total,
      };
      const { data: order } = await api.post("/orders", payload);

      if (payMethod === "cod") {
        placedRef.current = true;
        navigate(`/order-success/${order.id}`);
        setTimeout(() => clear(), 0);
        toast.success("Order placed! We'll confirm shortly.");
        return;
      }

      // Online - Razorpay
      const { data: rk } = await api.get("/payments/razorpay/key");
      if (!rk.enabled || !rk.key_id) {
        toast.error("Online payment not configured yet. Please choose Cash on Delivery or contact admin.");
        setPlacing(false);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Failed to load payment SDK"); setPlacing(false); return; }

      const rzp = new window.Razorpay({
        key: rk.key_id,
        amount: Math.round(total * 100),
        currency: "INR",
        name: "Eko Cakes & Bakes",
        description: `Order ${order.id.slice(0, 8)}`,
        order_id: order.razorpay_order_id,
        prefill: { name: address.full_name, email: user.email, contact: address.phone },
        theme: { color: "#8C3A3A" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            placedRef.current = true;
            navigate(`/order-success/${order.id}`);
            setTimeout(() => clear(), 0);
            toast.success("Payment successful!");
          } catch (e) { toast.error("Payment verification failed"); }
        },
        modal: { ondismiss: () => setPlacing(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to place order");
      setPlacing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2 space-y-10">
        <div>
          <div className="overline">Step 1</div>
          <h2 className="font-display text-3xl mt-2">Delivery Address</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <input data-testid="addr-name" placeholder="Recipient full name" className="input-eko" value={address.full_name} onChange={(e) => setAddress({ ...address, full_name: e.target.value })} />
            <input data-testid="addr-phone" placeholder="Phone" className="input-eko" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} />
            <input data-testid="addr-line1" placeholder="Address line 1" className="input-eko md:col-span-2" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} />
            <input data-testid="addr-line2" placeholder="Address line 2 (optional)" className="input-eko md:col-span-2" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} />
            <input data-testid="addr-city" placeholder="City" className="input-eko" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} />
            <input data-testid="addr-state" placeholder="State" className="input-eko" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} />
            <input data-testid="addr-pincode" placeholder="Pincode" className="input-eko" value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value })} />
          </div>
        </div>

        <div>
          <div className="overline">Step 2</div>
          <h2 className="font-display text-3xl mt-2">Delivery Slot</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="date-picker-btn" className="input-eko text-left flex items-center justify-between">
                  <span>{format(date, "PPP")}</span>
                  <CalendarIcon className="w-4 h-4 opacity-60" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <select data-testid="slot-select" value={slot} onChange={(e) => setSlot(e.target.value)} className="input-eko">
              {slots.map((s) => <option key={s.id} value={s.label}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <div className="overline">Step 3</div>
          <h2 className="font-display text-3xl mt-2">Payment</h2>
          <div className="mt-6 space-y-3">
            {onlineEnabled && (
              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer ${payMethod === "online" ? "border-[#8C3A3A] bg-[#8C3A3A]/5" : "border-[#E5DFD3]"}`} data-testid="pay-online">
                <input type="radio" name="pay" checked={payMethod === "online"} onChange={() => setPayMethod("online")} className="mt-1" />
                <div>
                  <div className="font-semibold">Pay Online (UPI, Cards, Netbanking, Wallets)</div>
                  <div className="text-sm text-[#6C665F]">Secure Razorpay checkout. Instant confirmation.</div>
                </div>
              </label>
            )}
            <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer ${payMethod === "cod" ? "border-[#8C3A3A] bg-[#8C3A3A]/5" : "border-[#E5DFD3]"}`} data-testid="pay-cod">
              <input type="radio" name="pay" checked={payMethod === "cod"} onChange={() => setPayMethod("cod")} className="mt-1" />
              <div>
                <div className="font-semibold">Cash on Delivery</div>
                <div className="text-sm text-[#6C665F]">Pay in cash or UPI to the delivery partner.</div>
              </div>
            </label>
            {!onlineEnabled && (
              <p className="text-xs text-[#6C665F] italic">Online payments (UPI / Cards / Netbanking) coming soon.</p>
            )}
          </div>
        </div>
      </div>

      <aside className="lg:sticky lg:top-24 h-fit card-eko p-6">
        <div className="overline mb-4">Order Summary</div>
        {items.map((it, i) => (
          <div key={i} className="flex justify-between text-sm py-1">
            <span className="text-[#6C665F]">{it.name} × {it.quantity}{it.weight ? ` · ${it.weight}` : ""}</span>
            <span>₹{it.price * it.quantity}</span>
          </div>
        ))}
        <div className="border-t border-[#E5DFD3] mt-4 pt-3 flex justify-between text-sm"><span>Subtotal</span><span>₹{subtotal}</span></div>
        <div className="flex justify-between text-sm"><span>Delivery</span><span>₹{deliveryFee}</span></div>
        <div className="border-t border-[#E5DFD3] mt-3 pt-3 flex justify-between text-lg font-semibold"><span>Total</span><span data-testid="checkout-total">₹{total}</span></div>
        <button data-testid="place-order-btn" onClick={placeOrder} disabled={placing} className="btn-primary w-full mt-6 justify-center">
          {placing ? "Processing…" : payMethod === "cod" ? "Place Order (COD)" : `Pay ₹${total}`}
        </button>
      </aside>
    </div>
  );
}
